import React, { useEffect, useState } from 'react';
import { Pressable, View, type ViewStyle } from 'react-native';
import { currentFinancialYear, currentPeriod, periodLabel, type PayComponent } from '@hrms/shared';
import { addMonths, pad } from '../../../lib/date';
import { Select, Text, TextField, radius, useTheme, type Option, type TextFieldProps, type Tone } from '../../../ui';

/** Month (YYYY-MM) picker: `past` months back and `future` months ahead of the current month. */
export function PeriodSelect({ value, onChange, label, past = 12, future = 2, testID }: { value: string; onChange: (p: string) => void; label?: string; past?: number; future?: number; testID?: string }) {
  const cur = currentPeriod();
  const opts = Array.from({ length: past + future + 1 }, (_, i) => addMonths(cur, future - i));
  if (!opts.includes(value)) opts.unshift(value);
  return <Select label={label} testID={testID} value={value} onChange={onChange} options={opts.map((p) => ({ value: p, label: periodLabel(p) }))} />;
}

/** Financial years: next year, current, and `past` previous years. */
export function fyList(past = 4, includeNext = true): string[] {
  const start = Number(currentFinancialYear().slice(0, 4));
  const years = Array.from({ length: past + 1 }, (_, i) => start - i);
  if (includeNext) years.unshift(start + 1);
  return years.map((y) => `${y}-${pad((y + 1) % 100)}`);
}

export function FySelect({ value, onChange, label, includeNext = false, testID }: { value: string; onChange: (fy: string) => void; label?: string; includeNext?: boolean; testID?: string }) {
  const opts = fyList(4, includeNext);
  if (!opts.includes(value)) opts.unshift(value);
  return <Select label={label} testID={testID} value={value} onChange={onChange} options={opts.map((f) => ({ value: f, label: `FY ${f}` }))} />;
}

/**
 * Numeric text field that keeps the typed text locally (so "1." or "" survive re-renders) and
 * reports a number (or null when empty and `allowEmpty`).
 */
export function NumField({ value, onChange, allowEmpty, width, ...rest }: Omit<TextFieldProps, 'value' | 'onChangeText'> & { value: number | null | undefined; onChange: (n: number | null) => void; allowEmpty?: boolean; width?: number }) {
  const [text, setText] = useState(value == null ? '' : String(value));
  useEffect(() => {
    const parsed = text === '' || text === '-' ? null : Number(text);
    if ((value ?? null) !== parsed) setText(value == null ? '' : String(value));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);
  return (
    <TextField
      keyboardType="numeric"
      inputMode="decimal"
      {...rest}
      containerStyle={width ? { width, ...(rest.containerStyle ?? {}) } : rest.containerStyle}
      value={text}
      onChangeText={(t) => {
        const clean = t.replace(/[^0-9.-]/g, '');
        setText(clean);
        if (clean === '' || clean === '-') onChange(allowEmpty ? null : 0);
        else if (!Number.isNaN(Number(clean))) onChange(Number(clean));
      }}
    />
  );
}

/** Toggleable pill (used for week-day / month pickers and multi-selects). */
export function Chip({ label, selected, onPress, disabled, testID }: { label: string; selected: boolean; onPress: () => void; disabled?: boolean; testID?: string }) {
  const { colors } = useTheme();
  return (
    <Pressable
      testID={testID}
      accessibilityRole="checkbox"
      accessibilityState={{ checked: selected, disabled }}
      disabled={disabled}
      onPress={onPress}
      style={({ pressed }) => ({
        paddingHorizontal: 12,
        paddingVertical: 6,
        borderRadius: radius.pill,
        borderWidth: 1,
        borderColor: selected ? colors.primary : colors.borderStrong,
        backgroundColor: selected ? colors.primarySoft : pressed ? colors.hover : colors.surface,
        opacity: disabled ? 0.6 : 1,
      })}
    >
      <Text size={13} weight={selected ? 'semibold' : 'medium'} color={selected ? colors.primary : colors.text}>
        {label}
      </Text>
    </Pressable>
  );
}

/** Label / amount line used in payslip and settlement breakdowns. */
export function AmountLine({ label, value, strong, color, sub }: { label: string; value: string; strong?: boolean; color?: string; sub?: string }) {
  return (
    <View style={{ flexDirection: 'row', justifyContent: 'space-between', gap: 12, paddingVertical: 4 }}>
      <View style={{ flex: 1 }}>
        <Text weight={strong ? 'semibold' : 'regular'}>{label}</Text>
        {!!sub && <Text variant="small">{sub}</Text>}
      </View>
      <Text weight={strong ? 'bold' : 'medium'} color={color} style={{ fontVariant: ['tabular-nums'] }}>
        {value}
      </Text>
    </View>
  );
}

/** Small caption + value block (for header summaries). */
export function KeyValue({ label, value, color, style }: { label: string; value: React.ReactNode; color?: string; style?: ViewStyle }) {
  return (
    <View style={[{ gap: 2, minWidth: 120 }, style]}>
      <Text variant="small">{label}</Text>
      {typeof value === 'string' || typeof value === 'number' ? (
        <Text variant="title" color={color} style={{ fontVariant: ['tabular-nums'] }}>
          {value}
        </Text>
      ) : (
        value
      )}
    </View>
  );
}

export function Mono({ children, color }: { children: React.ReactNode; color?: string }) {
  return (
    <Text variant="mono" color={color} size={12}>
      {children}
    </Text>
  );
}

export const COMPONENT_TYPES: PayComponent['type'][] = ['earning', 'deduction', 'employer_contribution', 'reimbursement'];
export const CALCULATIONS: PayComponent['calculation'][] = ['formula', 'fixed', 'balancing', 'statutory', 'input'];
export const TYPE_TONE: Record<PayComponent['type'], Tone> = { earning: 'success', deduction: 'danger', employer_contribution: 'info', reimbursement: 'warning' };
export const TYPE_LABEL: Record<PayComponent['type'], string> = { earning: 'Earnings', deduction: 'Deductions', employer_contribution: 'Employer contributions', reimbursement: 'Reimbursements' };

export const INDIAN_STATES: { code: string; name: string }[] = [
  ['AN', 'Andaman & Nicobar'], ['AP', 'Andhra Pradesh'], ['AR', 'Arunachal Pradesh'], ['AS', 'Assam'], ['BR', 'Bihar'],
  ['CH', 'Chandigarh'], ['CG', 'Chhattisgarh'], ['DN', 'Dadra & Nagar Haveli and Daman & Diu'], ['DL', 'Delhi'], ['GA', 'Goa'],
  ['GJ', 'Gujarat'], ['HR', 'Haryana'], ['HP', 'Himachal Pradesh'], ['JK', 'Jammu & Kashmir'], ['JH', 'Jharkhand'],
  ['KA', 'Karnataka'], ['KL', 'Kerala'], ['LA', 'Ladakh'], ['LD', 'Lakshadweep'], ['MP', 'Madhya Pradesh'],
  ['MH', 'Maharashtra'], ['MN', 'Manipur'], ['ML', 'Meghalaya'], ['MZ', 'Mizoram'], ['NL', 'Nagaland'],
  ['OD', 'Odisha'], ['PY', 'Puducherry'], ['PB', 'Punjab'], ['RJ', 'Rajasthan'], ['SK', 'Sikkim'],
  ['TN', 'Tamil Nadu'], ['TS', 'Telangana'], ['TR', 'Tripura'], ['UP', 'Uttar Pradesh'], ['UK', 'Uttarakhand'], ['WB', 'West Bengal'],
].map(([code, name]) => ({ code, name }));

export const stateName = (c: string) => INDIAN_STATES.find((s) => s.code === c)?.name ?? c;
export const stateOptions = (exclude: string[] = []): Option[] =>
  INDIAN_STATES.filter((s) => !exclude.includes(s.code)).map((s) => ({ value: s.code, label: `${s.name} (${s.code})` }));

export const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/** Immutable nested set by path. */
export function setIn<T>(obj: T, path: (string | number)[], value: unknown): T {
  const clone = JSON.parse(JSON.stringify(obj)) as Record<string | number, unknown>;
  let cur = clone;
  path.slice(0, -1).forEach((k) => {
    cur[k] = cur[k] ?? {};
    cur = cur[k] as Record<string | number, unknown>;
  });
  cur[path[path.length - 1]] = value;
  return clone as T;
}

export function getIn(obj: unknown, path: (string | number)[]): unknown {
  let v: unknown = obj;
  for (const k of path) v = (v as Record<string | number, unknown> | undefined)?.[k];
  return v;
}

export const clone = <T,>(v: T): T => JSON.parse(JSON.stringify(v)) as T;

/**
 * Responsive grid (as many columns of at least `min` px as fit). Same API as the kit's `Grid`,
 * but item widths are floored so sub-pixel layout widths never wrap the last column.
 */
export function AutoGrid({ min = 200, gap = 12, maxCols, children, style, testID }: { min?: number; gap?: number; maxCols?: number; children: React.ReactNode; style?: ViewStyle; testID?: string }) {
  const [w, setW] = useState(0);
  let cols = w ? Math.max(1, Math.floor((w + gap) / (min + gap))) : 1;
  if (maxCols) cols = Math.min(cols, maxCols);
  const itemW = w ? Math.floor((w - gap * (cols - 1)) / cols) - 1 : undefined;
  const items = React.Children.toArray(children).filter(Boolean);
  return (
    <View testID={testID} onLayout={(e) => setW(e.nativeEvent.layout.width)} style={[{ flexDirection: 'row', flexWrap: 'wrap', gap }, style]}>
      {items.map((child, i) => (
        <View key={i} style={itemW && cols > 1 ? { width: itemW } : { width: '100%' }}>
          {child}
        </View>
      ))}
    </View>
  );
}
